import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Warding Cores (BOSS-REWORK.md §B9 — the answerable card).
 *
 * Almost every card in the library poses a question with exactly one verb in
 * the answer: dodge. This one has two. The boss plants a handful of fat, slow,
 * SHOOTABLE cores around the player and holds them there; when the hold
 * expires each surviving core bursts into a ring. Shoot a core down and its
 * ring never happens — and the core pays out as a mote on the way (Game's
 * `bullet:destroyed` handler), so answering is rewarded rather than merely
 * permitted.
 *
 * Everything it needs already existed by the time it was written: `holdFor`
 * and `splitAfter`/`splitInto` are §B3's motion verbs, `shootable`/`hp` is the
 * bullet subtype, and the payout rides bulletCancel's conversion. The card is
 * the demonstration, not the machinery.
 *
 * Fair by construction: the cores are planted AROUND the player rather than on
 * them, at a radius they can already be standing outside of, and the burst is
 * a plain even ring — the decision the card asks for is "spend shots on the
 * board or spend movement on it", never "guess".
 */
export class WardingCores extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Warding Cores', telegraphDuration: 1.1, activeDuration: 2.4, minPhase });
    this.config = config;
  }

  onBegin(boss, player) {
    const { count, plantRadius } = this.config;
    const base = Math.random() * Math.PI * 2;
    this._sites = Array.from({ length: count }, (_, i) => {
      const angle = base + (i / count) * Math.PI * 2;
      return {
        x: player.x + Math.cos(angle) * plantRadius,
        y: player.y + Math.sin(angle) * plantRadius,
      };
    });
  }

  onActiveStart(_boss, _player, pool) {
    const { coreHp, coreRadius, holdFor, ringCount, ringSpeed, damage } = this.config;
    for (const site of this._sites) {
      // One bullet per core: it parks (holdFor), then splits into its ring.
      // Firing the ring as a SPLIT rather than as a second pattern beat is
      // what makes shooting the core actually cancel it — a destroyed bullet
      // never reaches its split.
      pool.fire({
        x: site.x,
        y: site.y,
        // The heading the ring is dealt around; the hold means it never
        // travels on it, so the direction is cosmetic until the burst.
        vx: ringSpeed,
        vy: 0,
        radius: coreRadius,
        damage,
        color: '#ffd479',
        lifetime: holdFor + 2.6, // the ring inherits what's left, so this is really the ring's range
        holdFor,
        // Counted from the LAUNCH, not from the spawn: a held bullet's timers
        // are frozen for the hold (see BossBullet.update), so a core bursts
        // the instant it would otherwise have started moving.
        splitAfter: 0.01,
        splitInto: ringCount,
        splitSpread: Math.PI * 2 * (1 - 1 / ringCount), // an even ring, no doubled bullet at the seam
        shootable: true,
        hp: coreHp,
      });
    }
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH) return null;
    return {
      kind: 'prismMines', // the existing "something is about to happen here" marker
      mines: this._sites.map((site) => ({ ...site, radius: this.config.coreRadius * 2 })),
      imminent: false,
    };
  }
}
